/**
 * Sentry Error Tracking Service
 * 
 * Minimal Sentry implementation for error tracking (exceptions and messages only)
 * Analytics tracking is handled by Mixpanel
 */

import * as Sentry from '@sentry/react';
import type { SeverityLevel } from '@sentry/react';
import type React from 'react';
import { trackEvent as mixpanelTrackEvent } from './mixpanel';
import { isDevelopmentMode } from '@/lib/utils';

let isInitialized = false;

/**
 * Initialize Sentry with DSN and configuration
 * Should be called once at application startup
 */
export const initSentry = (): void => {
  const dsn = import.meta.env.VITE_REACT_APP_SENTRY_DSN;

  if (!dsn) {
    console.warn('[Sentry] DSN not found, Sentry will not be initialized');
    return;
  }

  // Never initialize Sentry when running the dev server (yarn dev / npm run dev)
  if (import.meta.env.DEV) {
    console.log('[Sentry] Disabled when running the dev server');
    return;
  }

  // Skip initialization in development/vendor builds if explicitly disabled
  const enableDev = import.meta.env.VITE_ENABLE_SENTRY_DEV === 'true';
  const isDev = isDevelopmentMode();
  if (isDev && !enableDev) {
    console.log('[Sentry] Disabled in development mode');
    return;
  }

  try {
    // Determine environment
    const environment = import.meta.env.MODE === 'production' ? 'production' :
                       import.meta.env.MODE === 'staging' ? 'staging' :
                       import.meta.env.MODE === 'vendor' ? 'vendor' : 'development';

    Sentry.init({
      dsn,
      environment,
      sendDefaultPii: false, // Don't send IP addresses and other default PII
      // Disable Sentry session replay (using Mixpanel for session replay instead)
      replaysSessionSampleRate: 0,
      replaysOnErrorSampleRate: 0,
      beforeSend(event) {
        // Filter sensitive data from URLs
        if (event.request?.url) {
          // Remove query parameters that might contain sensitive data
          try {
            const url = new URL(event.request.url);
            url.search = ''; // Remove all query parameters
            event.request.url = url.toString();
          } catch {
            // If URL parsing fails, just remove everything after ?
            event.request.url = event.request.url.split('?')[0];
          }
        }

        // Remove sensitive headers
        if (event.request?.headers) {
          delete event.request.headers['authorization'];
          delete event.request.headers['Authorization'];
          delete event.request.headers['cookie'];
          delete event.request.headers['Cookie'];
        }

        return event;
      },
    });

    isInitialized = true;
  } catch (error) {
    console.error('[Sentry] Initialization failed:', error);
  }
};

/**
 * Set user identification in Sentry
 * Only sends user ID (Cognito sub), no PII
 */
export const setSentryUser = (userId: string): void => {
  if (!isInitialized) {
    return;
  }

  try {
    Sentry.setUser({
      id: userId,
      // Explicitly don't send email or username
    });
  } catch (error) {
    console.error('[Sentry] Failed to set user:', error);
  }
};

/**
 * Clear user identification in Sentry
 */
export const clearSentryUser = (): void => {
  if (!isInitialized) {
    return;
  }

  try {
    Sentry.setUser(null);
  } catch (error) {
    console.error('[Sentry] Failed to clear user:', error);
  }
};

// Sentry utilities wrapper (maintains existing API)
export const sentryUtils = {
  setUser: (user: { id: string; email?: string; username?: string; role?: string }) => {
    // Only use id, ignore email/username for privacy
    setSentryUser(user.id);
  },
  clearUser: () => {
    clearSentryUser();
  },
  setTag: (_key: string, _value: string) => {
    if (!isInitialized) return;
    try {
      Sentry.setTag(_key, _value);
    } catch (error) {
      console.error('[Sentry] Failed to set tag:', error);
    }
  },
  setContext: (_key: string, _context: Record<string, unknown>) => {
    if (!isInitialized) return;
    try {
      Sentry.setContext(_key, _context);
    } catch (error) {
      console.error('[Sentry] Failed to set context:', error);
    }
  },
  addBreadcrumb: (_message: string, _category?: string, _level?: SeverityLevel, _data?: Record<string, unknown>) => {
    if (!isInitialized) return;
    try {
      Sentry.addBreadcrumb({
        message: _message,
        category: _category,
        level: _level,
        data: _data,
      });
    } catch (error) {
      console.error('[Sentry] Failed to add breadcrumb:', error);
    }
  },
  captureMessage: (message: string, level?: SeverityLevel) => {
    if (!isInitialized) return undefined;
    try {
      Sentry.captureMessage(message, level);
    } catch (error) {
      console.error('[Sentry] Failed to capture message:', error);
    }
    return undefined;
  },
  captureException: (error: Error, context?: Record<string, unknown>) => {
    if (!isInitialized) return undefined;
    try {
      Sentry.captureException(error, {
        contexts: context ? { custom: context } : undefined,
      });
    } catch (err) {
      console.error('[Sentry] Failed to capture exception:', err);
    }
    return undefined;
  },
  captureEvent: (_event: unknown) => {
    // Stub - not needed for minimal implementation
    return undefined;
  },
  withErrorBoundary: <P extends object>(Component: React.ComponentType<P>) => Component,
  startSpan: <T>(_options: { name: string; op?: string }, callback: () => T) => callback(),
  withScope: (_callback: (scope: unknown) => void) => {
    // Stub - not needed for minimal implementation
  },
};

/**
 * Capture an exception in Sentry
 */
export const captureException = (error: Error, context?: Record<string, unknown>): void => {
  sentryUtils.captureException(error, context);
};

/**
 * Capture a message in Sentry
 */
export const captureMessage = (message: string, level?: SeverityLevel): void => {
  sentryUtils.captureMessage(message, level);
};

/**
 * Track user action - routes to Mixpanel
 */
export const trackUserAction = (action: string, data?: Record<string, unknown>): void => {
  mixpanelTrackEvent(action, data);
};

/**
 * Track navigation - routes to Mixpanel
 */
export const trackNavigation = (from: string, to: string): void => {
  mixpanelTrackEvent('navigation', { from, to });
};

/**
 * Track API call - routes to Mixpanel
 */
export const trackAPICall = (endpoint: string, method: string, status?: number, duration?: number): void => {
  mixpanelTrackEvent('api_call', {
    endpoint,
    method,
    status,
    duration,
  });
};

/**
 * Capture API error with request/response context
 */
export const captureAPIError = (
  error: Error,
  requestData?: {
    url?: string;
    method?: string;
    headers?: Record<string, string>;
    data?: unknown;
    params?: Record<string, unknown>;
  },
  responseData?: {
    status?: number;
    statusText?: string;
    headers?: Record<string, string>;
    data?: unknown;
  }
): void => {
  const context: Record<string, unknown> = {};

  if (requestData) {
    context.request = {
      url: requestData.url,
      method: requestData.method,
      // Don't include headers/data/params to avoid PII
    };
  }

  if (responseData) {
    context.response = {
      status: responseData.status,
      statusText: responseData.statusText,
      // Don't include headers/data to avoid PII
    };
  }

  captureException(error, context);
};

/**
 * Measure performance - stub (not needed for minimal implementation)
 */
export const measurePerformance = async <T>(
  _operationName: string,
  operation: () => Promise<T>
): Promise<T> => {
  return operation();
};

/**
 * Feedback utilities - stubs (not needed for minimal implementation)
 */
export const feedbackUtils = {
  showFeedbackDialog: () => {},
  createFeedback: (_message: string, _name?: string, _email?: string, _url?: string) => {},
  captureFeedbackWithContext: (
    _message: string,
    _options?: {
      name?: string;
      email?: string;
      tags?: Record<string, string>;
      extra?: Record<string, unknown>;
    }
  ) => {},
};
